import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { WarningNotice } from '../../statementMeta';

type StatementWarningsProps = {
    /**
     * The advisories, most important first. A `localized` notice is written
     * here from this app's own locale files; a `raw` one is the server's own
     * sentence, shown only for a rule this app has no text for (BOS-163).
     */
    notices: WarningNotice[];
    /**
     * Codes of postable accounts the ledger's statement layout maps nowhere.
     * Shown as one counted line rather than one advisory each.
     */
    unmappedCodes?: string[];
    onDismiss: () => void;
    /** Literal testid prefix, e.g. `balance-sheet` / `profit-loss`. */
    testIdPrefix: string;
};

/**
 * Advisory plausibility notices — informational, never a failure. A calm,
 * dismissible banner on a neutral surface with an amber lead-in icon and a bold
 * "Advisory." label (`role="status"`), so it reads as guidance, not an error.
 *
 * Two things differ from the `/accounting` original, both learned from a real
 * chart outgrowing its statement layout:
 *
 * 1. Messages are a **list**, not `join(' ')`. Thirty advisories concatenated
 *    into one paragraph is a wall of prose, and the notice that matters — the
 *    Balance Sheet not balancing — is buried mid-sentence.
 * 2. Unmapped accounts get **one counted line** listing their codes, because
 *    the server sends one warning per account and there can be dozens. It sits
 *    apart and is not dismissible: the others are advisories about a statement,
 *    this one says the statement is *incomplete*.
 */
export const StatementWarnings: React.FC<StatementWarningsProps> = ({
    notices,
    unmappedCodes = [],
    onDismiss,
    testIdPrefix,
}) => {
    const { t } = useTranslation('pages');

    if (notices.length === 0 && unmappedCodes.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            {notices.length > 0 ? (
                <div
                    className="flex items-start gap-3 rounded-xl border border-separator bg-surface-secondary px-4 py-3"
                    role="status"
                    {...getNodeTestIdProp(`${testIdPrefix}-warnings`)}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0 text-warning"
                        icon="gravity-ui:triangle-exclamation"
                    />
                    <div className="flex flex-1 flex-col gap-1">
                        <span className="text-sm font-semibold text-foreground">
                            {t('statements.warnings.label')}
                        </span>
                        <ul className="flex flex-col gap-1">
                            {notices.map((notice, index) => (
                                <li
                                    className="text-sm text-foreground"
                                    // Index-keyed on purpose: two advisories can
                                    // carry identical text, and the list is
                                    // replaced wholesale on every fetch.
                                    key={`${index}-${
                                        notice.kind === 'localized'
                                            ? notice.key
                                            : notice.text
                                    }`}
                                >
                                    {notice.kind === 'localized'
                                        ? t(notice.key, notice.params)
                                        : notice.text}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Button
                        aria-label={t('statements.warnings.dismiss')}
                        isIconOnly
                        onPress={onDismiss}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp(
                            `${testIdPrefix}-warnings-dismiss`,
                        )}
                    >
                        <Icon aria-hidden icon="gravity-ui:xmark" />
                    </Button>
                </div>
            ) : null}

            {unmappedCodes.length > 0 ? (
                <div
                    className="flex items-start gap-3 rounded-xl border border-separator bg-surface-secondary px-4 py-3"
                    role="status"
                    {...getNodeTestIdProp(`${testIdPrefix}-unmapped`)}
                >
                    <Icon
                        aria-hidden
                        className="mt-0.5 shrink-0 text-warning"
                        icon="gravity-ui:circle-exclamation"
                    />
                    <div className="flex flex-1 flex-col gap-1">
                        <span className="text-sm font-semibold text-foreground">
                            {t('accounting.statements.unmapped.title', {
                                count: unmappedCodes.length,
                            })}
                        </span>
                        <p className="text-sm text-muted">
                            {t('accounting.statements.unmapped.body')}
                        </p>
                        <p
                            className="text-sm tabular-nums text-foreground"
                            {...getNodeTestIdProp(
                                `${testIdPrefix}-unmapped-codes`,
                            )}
                        >
                            {unmappedCodes.join(', ')}
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
